import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  ShieldCheck,
  Award,
  Calendar,
  ChevronRight,
  ArrowRight,
  ExternalLink,
  BookOpen,
  Share2,
  Check,
  Building,
  Layers,
  Sparkles,
  Eye,
  FileText,
  X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/global/SEO';

interface NewsroomPageProps {
  lang: 'en' | 'ar';
}

interface PressRelease {
  id: string;
  tagEn: string;
  tagAr: string;
  dateEn: string;
  dateAr: string;
  titleEn: string;
  titleAr: string;
  excerptEn: string;
  excerptAr: string;
  bodyEn: string[];
  bodyAr: string[];
  ctaUrl?: string;
  ctaTextEn?: string;
  ctaTextAr?: string;
  isFeatured?: boolean;
}

const PRESS_RELEASES: PressRelease[] = [
  {
    id: 'egipa-ip-registration-5007',
    tagEn: 'LEGAL & SOVEREIGN ASSET',
    tagAr: 'توثيق قانوني وسيادي',
    dateEn: 'July 27, 2026',
    dateAr: '27 يوليو 2026',
    titleEn: 'VEXL Secures Official Software Copyright & IP Protection for its GeoAI Ecosystem (EGIPA Reg. #5007)',
    titleAr: 'فكسل تعتمد حقوق الملكية الفكرية والأصول البرمجية السيادية لمنظومتها الجيومكانية لدى الجهاز المصري للملكية الفكرية (إيداع رقم 5007)',
    excerptEn: 'The Egyptian Intellectual Property Authority (EGIPA) officially grants computer software copyright protection (Deposit #5007) for the VEXL Ecosystem, recognizing its dual-engine architecture: VEXL NOVA and Drought Watch Pro.',
    excerptAr: 'اعتمد الجهاز المصري للملكية الفكرية (EGIPA) رسمياً تسجيل وتوثيق حقوق المؤلف للمصنف البرمجي "منظومة فكسل للذكاء الاصطناعي الجيومكاني" برقم إيداع (5007)، بما يشمل محركي VEXL NOVA و Drought Watch Pro.',
    bodyEn: [
      'CAIRO, EGYPT — July 27, 2026 — VEXL Spatial Intelligence Labs announced today that it has been granted official software copyright registration and intellectual property deposit (#5007) from the Egyptian Intellectual Property Authority (EGIPA), regulated under Law No. 82 of 2002 and Law No. 163 of 2023.',
      'The certified deposit officially protects the complete proprietary architecture of the VEXL ECOSYSTEM, developed in Python, TypeScript, JavaScript, and Node.js by Founder and Principal GeoAI Architect Abdelrahman Mohamed Diaaeldin Samir Abushousha.',
      'The registered software encompasses two core operational engines:',
      '1. VEXL NOVA Engine: An integrated sovereign geospatial analytics pipeline engineering the mathematical fusion of multi-dimensional vector geometries and raster arrays, automating spatial code generation and topological verification.',
      '2. Drought Watch Pro: A proprietary deep learning Bi-LSTM neural time-series framework processing multi-satellite data streams (MODIS, CHIRPS, GLDAS) to deliver 6-month agricultural drought forecasting with 91.3% verified model accuracy (recently awarded 3rd Prize globally at the 2026 UNEP-Tongji GESC Challenge).',
      'This milestone reinforces VEXL\'s commitment to developing 100% sovereign, enterprise-grade GeoAI software for government agencies, environmental ministries, and financial institutions worldwide.'
    ],
    bodyAr: [
      'القاهرة، مصر — 27 يوليو 2026 — أعلنت مختبرات فكسل للذكاء المكاني اليوم حصولها رسمياً على شهادة إيداع وحماية حقوق المؤلف للمصنف البرمجي "منظومة فكسل للذكاء الاصطناعي الجيومكاني" برقم (5007) من الجهاز المصري للملكية الفكرية (EGIPA) استناداً إلى قانون حماية الملكية الفكرية رقم (82) لسنة 2002 وقانون إنشاء الجهاز رقم (163) لسنة 2023.',
      'يحمي هذا التسجيل الرسمي البنية البرمجية المتكاملة لمنظومة VEXL ECOSYSTEM المطورة بلغات Python و TypeScript و JavaScript و Node.js بواسطة المؤسس ورئيس الأبحاث الهندسية عبدالرحمن محمد ضياء الدين سمير شوشة.',
      'وتضم المنظومة المسجلة حزمتين تشغيليتين رئيسيتين:',
      '1. حزمة VEXL NOVA: مساعد ذكي متكامل لأتمتة أكواد التحليل الجغرافي ودمج المتجهات والخلايا الرقمية والتحقق الطوبولوجي.',
      '2. حزمة Drought Watch Pro: منصة تنبؤ استباقي بيئي تعتمد على شبكات التعلم العميق (Bi-LSTM) لمعالجة السلاسل الزمنية للأقمار الصناعية والتنبؤ بالجفاف الزراعي قبل حدوثه بـ 6 أشهر بدقة 91.3% (والفائزة بالجائزة الثالثة عالمياً في تحدي GESC 2026 تحت مظلة UNEP-Tongji).',
      'تؤكد هذه الخطوة ريادة فكسل في بناء بنى برمجية سيادية ومملوكة بالكامل لخدمة القطاعات الحكومية والمؤسسات الكبرى وأمن الموارد الطبيعية.'
    ],
    ctaUrl: '/intellectual-property',
    ctaTextEn: 'View IP Dossier & Certificate',
    ctaTextAr: 'عرض ملف الملكية الفكرية والشهادة',
    isFeatured: true,
  },
  {
    id: 'gesc-unep-tongji-award-2026',
    tagEn: 'GLOBAL AWARDS & BENCHMARKS',
    tagAr: 'جوائز وتصنيفات عالمية',
    dateEn: 'August 12, 2026',
    dateAr: '12 أغسطس 2026',
    titleEn: 'DroughtWatch AI V2 Wins Global 3rd Prize at the 2026 Global Environment Solutions Challenge (UNEP–Tongji)',
    titleAr: 'نظام DroughtWatch AI V2 يفوز بالجائزة الثالثة عالمياً في تحدي الحلول البيئية العالمي 2026 (UNEP-Tongji)',
    excerptEn: 'Out of 574 international teams across 58 countries, DroughtWatch AI V2 was awarded Third Prize for pixel-level, six-month-ahead agricultural drought early warning in Egypt.',
    excerptAr: 'من بين 574 فريقاً بحثياً دولياً من 58 دولة، توج نظام DroughtWatch AI V2 بالجائزة الثالثة عالمياً للتنبؤ بالجفاف الزراعي على مستوى البكسل في جمهورية مصر العربية.',
    bodyEn: [
      'SHANGHAI / NAIROBI — August 2026 — VEXL is proud to announce that its flagship ecohydrological forecasting model, DroughtWatch AI V2, has been awarded Third Prize at the 2026 Global Environment Solutions Challenge (GESC), organized by the UNEP–Tongji Institute of Environment for Sustainable Development.',
      'The research, authored by Abdelrahman Abushousha, demonstrates a 28% error reduction over standard historical climatology baselines, explaining 87.6% of out-of-sample vegetation variance across 25,522 cropland pixels.',
      'The full technical preprints and model weights are publicly indexed in the VEXL Research Publications Archive.'
    ],
    bodyAr: [
      'شنغهاي / نيروبي — أغسطس 2026 — تفخر فكسل بإعلان فوز نموذجها البيئي الرائد DroughtWatch AI V2 بالجائزة الثالثة في تحدي الحلول البيئية العالمي (GESC 2026) المنظم تحت مظلة معهد البيئة للتنمية المستدامة المشترك بين برنامج الأمم المتحدة للبيئة (UNEP) وجامعة تونغجي.',
      'أثبت البحث، المنشور بقيادة عبدالرحمن شوشة، تخفيضاً في خطأ التنبؤ بنسبة 28% مقارنة بخط الأساس المناخي، وتفسيراً لـ 87.6% من التباين في الغطاء النباتي عبر 25,522 بكسل زراعي.',
      'جميع الأوراق العلمية والبيانات منشورة ومتاحة في أرشيف أبحاث فكسل.'
    ],
    ctaUrl: '/research/overview',
    ctaTextEn: 'Read Research Breakthrough',
    ctaTextAr: 'قراءة تفاصيل البحث العلمي',
    isFeatured: false,
  },
  {
    id: 'vexl-spatial-foundation-architecture',
    tagEn: 'PLATFORM RELEASES',
    tagAr: 'إصدارات المنصة',
    dateEn: 'July 15, 2026',
    dateAr: '15 يوليو 2026',
    titleEn: 'VEXL Unveils Dual-Vector Spatial RAG Engine with Topological Guardrails',
    titleAr: 'فكسل تطلق محرك الاستدلال المكاني المزدوج المدعوم بالأمان والتحقق الطوبولوجي',
    excerptEn: 'Enterprise GeoAI systems now feature mathematical topology verification, ensuring zero hallucinations in spatial boundary calculations and polygon operations.',
    excerptAr: 'تطبيق منظومة التحقق الطوبولوجي الرياضي المدمجة لضمان دقة قطعية تمنع أي أخطاء في معالجة المضلعات والحدود الجغرافية.',
    bodyEn: [
      'VEXL announced the general availability of its topological guardrails engine, enabling autonomous spatial agents to query and reason over planetary-scale vector datasets with absolute mathematical precision.',
      'The architecture natively integrates with PostgreSQL/PostGIS, Cloud-Optimized GeoTIFFs (COGs), and enterprise GIS backends.'
    ],
    bodyAr: [
      'أعلنت فكسل الإطلاق العام لمحرك الأمان الطوبولوجي الذي يتيح لعملاء الذكاء الاصطناعي الجغرافي استعلام وتحليل البيانات المكانية بدقة رياضية قطعية تمنع أي أخطاء هندسية.',
      'يتكامل المحرك أصلياً مع قواعد بيانات PostGIS، وتنسيقات COG السحابية، وأنظمة المعلومات الجغرافية للمؤسسات.'
    ],
    ctaUrl: '/products/nova',
    ctaTextEn: 'Explore NOVA Architecture',
    ctaTextAr: 'استكشف معمارية NOVA',
    isFeatured: false,
  }
];

export const NewsroomPage: React.FC<NewsroomPageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const [selectedArticle, setSelectedArticle] = useState<PressRelease | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleShare = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(`${window.location.origin}/news#${id}`);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2500);
    }
  };

  const featuredRelease = PRESS_RELEASES.find((p) => p.isFeatured) || PRESS_RELEASES[0];
  const regularReleases = PRESS_RELEASES.filter((p) => p.id !== featuredRelease.id);

  return (
    <div
      className={`min-h-screen bg-[#05070A] text-[#EDF0F1] selection:bg-[#0E93A3]/30 ${
        isAr ? 'font-arabic' : 'font-sans'
      } antialiased`}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <SEO
        title={isAr ? 'غرفة الأخبار والبيانات الصحفية | مختبرات فكسل' : 'Newsroom & Press Releases | VEXL Spatial Intelligence'}
        description={
          isAr
            ? 'آخر الأخبار الرسمية والبيانات الصحفية والتحديثات المؤسسية الصادرة عن شركة ومختبرات فكسل للذكاء الاصطناعي الجيومكاني.'
            : 'Official press releases, major corporate milestones, legal intellectual property announcements, and research awards from VEXL.'
        }
        lang={lang}
      />

      {/* ── Top Header Section ────────────────────────────────────────────── */}
      <section className="pt-20 md:pt-24 pb-16 border-b border-[#262C31]">
        <div className="w-full max-w-[1450px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-[#262C31]">
            <div className="space-y-2">
              <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold flex items-center gap-2">
                <Zap size={14} />
                <span>{isAr ? 'غرفة الأخبار والبيانات الصحفية الرسمية' : 'OFFICIAL NEWSROOM & PRESS RELEASES'}</span>
              </div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#EDF0F1] leading-[1.06]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {isAr ? 'أخبار وإعلانات فكسل' : 'VEXL Newsroom'}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/intellectual-property"
                className="px-4 py-2.5 rounded-sm border border-[#262C31] bg-[#12161A] hover:border-[#0E93A3]/50 text-xs font-medium text-[#EDF0F1] transition-all flex items-center gap-1.5"
              >
                <ShieldCheck size={14} className="text-[#0E93A3]" />
                <span>{isAr ? 'الملكية الفكرية (#5007)' : 'IP Dossier (#5007)'}</span>
              </Link>
              <Link
                to="/contact"
                className="px-4 py-2.5 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white text-xs font-semibold transition-all shadow-sm"
              >
                {isAr ? 'تواصل مع المكتب الإعلامي' : 'Press Contact'}
              </Link>
            </div>
          </div>

          <p className="text-base text-[#9AA5AC] max-w-3xl pt-6 leading-relaxed">
            {isAr
              ? 'البيانات الصحفية المعتمدة، الإعلانات القانونية والمؤسسية، التحديثات التقنية الكبرى، والجوائز العالمية الصادرة عن منظومة ومختبرات فكسل.'
              : 'Official announcements, legal IP milestones, product architecture updates, and global research achievements from VEXL Spatial Intelligence.'}
          </p>
        </div>
      </section>

      {/* ── Featured Press Release (Spotlight Hero) ────────────────────────── */}
      <section className="py-16 border-b border-[#262C31] bg-[#05070A]">
        <div className="w-full max-w-[1450px] mx-auto px-6">
          <div className="mb-6 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#0E93A3] animate-pulse" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-bold">
              {isAr ? 'البيان الصحفي الأبرز' : 'FEATURED PRESS RELEASE'}
            </span>
          </div>

          <div className="p-8 sm:p-10 rounded-md border border-[#262C31] bg-[#12161A] shadow-sm space-y-6 relative overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="px-3 py-1 rounded bg-[#0E93A3]/10 border border-[#0E93A3]/20 text-[#0E93A3] text-xs font-mono font-medium">
                  {isAr ? featuredRelease.tagAr : featuredRelease.tagEn}
                </span>
                <span className="text-xs font-mono text-[#9AA5AC]">{isAr ? featuredRelease.dateAr : featuredRelease.dateEn}</span>
              </div>

              <span className="text-xs font-mono text-[#9AA5AC]">EGIPA REGISTRY // #5007</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#EDF0F1] leading-snug">
              {isAr ? featuredRelease.titleAr : featuredRelease.titleEn}
            </h2>

            <p className="text-base text-[#9AA5AC] leading-relaxed max-w-4xl">
              {isAr ? featuredRelease.excerptAr : featuredRelease.excerptEn}
            </p>

            <div className="pt-4 flex flex-wrap items-center gap-4">
              <Link
                to={featuredRelease.ctaUrl || '/intellectual-property'}
                className="px-6 py-3 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white text-xs font-semibold transition-all shadow-sm flex items-center gap-2"
              >
                <span>{isAr ? featuredRelease.ctaTextAr : featuredRelease.ctaTextEn}</span>
                <ArrowRight size={14} className={isAr ? 'rotate-180' : ''} />
              </Link>

              <button
                onClick={() => setSelectedArticle(featuredRelease)}
                className="px-5 py-3 rounded-sm border border-[#262C31] bg-[#171C21] hover:border-[#0E93A3]/50 text-xs font-medium text-[#EDF0F1] transition-all flex items-center gap-2 cursor-pointer"
              >
                <BookOpen size={14} />
                <span>{isAr ? 'قراءة البيان الكامل' : 'Read Full Release'}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── All Press Releases Archive ──────────────────────────────────────── */}
      <section className="py-20 border-b border-[#262C31]">
        <div className="w-full max-w-[1450px] mx-auto px-6">
          <div className="mb-10 space-y-2">
            <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold">
              {isAr ? 'أرشيف الأخبار والبيانات' : 'ALL RELEASES & UPDATES'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#EDF0F1] tracking-tight">
              {isAr ? 'جميع البيانات والتحديثات الرسمية' : 'Official Communications Archive'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {regularReleases.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedArticle(item)}
                className="p-6 sm:p-8 rounded-md border border-[#262C31] bg-[#12161A] hover:border-[#0E93A3]/50 transition-all cursor-pointer space-y-4 group flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs font-mono text-[#9AA5AC]">
                    <span className="px-2.5 py-0.5 rounded bg-[#171C21] text-[#0E93A3] font-medium">
                      {isAr ? item.tagAr : item.tagEn}
                    </span>
                    <span>{isAr ? item.dateAr : item.dateEn}</span>
                  </div>

                  <h3 className="text-lg sm:text-xl font-bold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors leading-snug">
                    {isAr ? item.titleAr : item.titleEn}
                  </h3>

                  <p className="text-xs text-[#9AA5AC] leading-relaxed line-clamp-3">
                    {isAr ? item.excerptAr : item.excerptEn}
                  </p>
                </div>

                <div className="pt-4 border-t border-[#262C31] flex items-center justify-between text-xs font-semibold text-[#0E93A3]">
                  <span>{isAr ? 'اقرأ المزيد' : 'Read Announcement'}</span>
                  <ChevronRight size={14} className={`group-hover:translate-x-1 transition-transform ${isAr ? 'rotate-180' : ''}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Article Reader Modal ────────────────────────────────────────────── */}
      <AnimatePresence>
        {selectedArticle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-3xl rounded-lg border border-[#262C31] bg-[#12161A] p-6 sm:p-8 shadow-2xl text-[#EDF0F1] my-8 max-h-[90vh] overflow-y-auto relative"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedArticle(null)}
                className="absolute top-6 right-6 p-2 rounded text-[#9AA5AC] hover:text-[#EDF0F1] hover:bg-[#171C21] transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>

              <div className="space-y-6">
                <div className="flex flex-wrap items-center gap-3 pr-10">
                  <span className="px-3 py-1 rounded bg-[#0E93A3]/10 border border-[#0E93A3]/20 text-[#0E93A3] text-xs font-mono font-bold">
                    {isAr ? selectedArticle.tagAr : selectedArticle.tagEn}
                  </span>
                  <span className="text-xs font-mono text-[#9AA5AC]">{isAr ? selectedArticle.dateAr : selectedArticle.dateEn}</span>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-[#EDF0F1] leading-snug">
                  {isAr ? selectedArticle.titleAr : selectedArticle.titleEn}
                </h3>

                <div className="space-y-4 text-xs sm:text-sm text-[#9AA5AC] leading-relaxed pt-4 border-t border-[#262C31]">
                  {(isAr ? selectedArticle.bodyAr : selectedArticle.bodyEn).map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>

                {/* Footer Action */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-[#262C31]">
                  {selectedArticle.ctaUrl ? (
                    <Link
                      to={selectedArticle.ctaUrl}
                      onClick={() => setSelectedArticle(null)}
                      className="px-5 py-2.5 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white text-xs font-semibold transition-all flex items-center gap-2"
                    >
                      <span>{isAr ? selectedArticle.ctaTextAr : selectedArticle.ctaTextEn}</span>
                      <ArrowRight size={13} className={isAr ? 'rotate-180' : ''} />
                    </Link>
                  ) : <div />}

                  <button
                    onClick={() => setSelectedArticle(null)}
                    className="px-5 py-2.5 rounded-sm border border-[#262C31] bg-[#171C21] hover:border-[#0E93A3]/40 text-xs font-medium text-[#EDF0F1] cursor-pointer"
                  >
                    {isAr ? 'إغلاق' : 'Close'}
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default NewsroomPage;
